<template>
  <div class="jim-scheduling-container">
    <div class="gtin-page">
      <h1 class="page-heading">GTIN & Label Setup</h1>

      <div class="gtin-layout">
        <!-- LEFT SIDE CONTROL PANEL -->
        <div class="left-side-section">
          <div class="control">
            <label>Search Item</label>
            <input
              type="text"
              v-model="searchText"
              placeholder="AMB25..."
              @input="currentPage = 1"
            />
          </div>

          <div class="control">
            <label>Status</label>
            <select v-model="statusFilter" @change="currentPage = 1">
              <option value="ALL">All</option>
              <option value="CONFIGURED">Configured</option>
              <option value="PARTIAL">Partial</option>
              <option value="BLANK">Blank</option>
            </select>
          </div>

          <button class="plan-it-btn" @click="loadAll" :disabled="loading">
            Refresh
          </button>

          <button
            class="plan-it-btn pablotify-btn"
            @click="$refs.pablotify.openPopup()"
          >
            Comm Center
            <span v-if="pablotifyUnread > 0" class="notif-badge">
              🔔 {{ pablotifyUnread }}
            </span>
          </button>

          <PabloQuacious
            ref="pablotify"
            :dept="'Labels'"
            :loc="selectedLocation"
            :showButton="false"
            @unread-count="pablotifyUnread = $event"
          />

          <div class="side-help">
            <div><strong>Total:</strong> {{ records.length }}</div>
            <div><strong>Showing:</strong> {{ filteredRecords.length }}</div>
            <div><strong>Configured:</strong> {{ configuredCount }}</div>
            <div><strong>Partial:</strong> {{ partialCount }}</div>
            <div><strong>Blank:</strong> {{ blankCount }}</div>
          </div>
        </div>

        <!-- RIGHT SIDE CONTENT -->
        <div class="gtin-wrapper">
          <div class="summary-bar">
            <div class="summary-item">
              <div class="summary-label">Records</div>
              <div class="summary-value">{{ records.length }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Configured</div>
              <div class="summary-value">{{ configuredCount }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Partial</div>
              <div class="summary-value">{{ partialCount }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Blank</div>
              <div class="summary-value">{{ blankCount }}</div>
            </div>
          </div>

          <div class="table-panel">
            <div class="table-header-row">
              <div>
                <div class="small-muted" v-if="loading">Loading label setup…</div>
                <div class="small-muted" v-else>
                  GTIN / label records from Macola 100 — dbo.GTIN_BarcodeData
                </div>
              </div>

              <div class="pager">
                <button class="btn btn-small" @click="prevPage" :disabled="currentPage <= 1">
                  Prev
                </button>
                <span class="page-text">
                  Page {{ currentPage }} / {{ totalPages }}
                </span>
                <button class="btn btn-small" @click="nextPage" :disabled="currentPage >= totalPages">
                  Next
                </button>
              </div>
            </div>

            <div class="table-scroll">
              <table class="gtin-table">
                <thead>
                  <tr>
                    <th>Item No</th>
                    <th>Status</th>
                    <th>UPC ID</th>
                    <th>GTIN-14</th>
                    <th>UPC Qty</th>
                    <th>PL 1</th>
                    <th>PL 2</th>
                    <th>PL 3</th>
                    <th>PL 4</th>
                    <th>PL 5</th>
                    <th>PL 6</th>
                    <th>PL 7</th>
                    <th>Label Form</th>
                    <th>Logistic Label</th>
                    <th>Finished Label</th>
                    <th>WiSys</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="row in pagedRecords" :key="row.Item_ID">
                    <td class="item-cell">{{ row.Item_ID }}</td>

                    <td>
                      <span class="status-pill" :class="statusClass(row)">
                        {{ recordStatus(row) }}
                      </span>
                    </td>

                    <td>{{ displayVal(row.UPC_Item_ID) }}</td>
                    <td class="gtin-cell">{{ displayVal(row.GTIN_14_Code) }}</td>
                    <td>{{ displayVal(row.UPC_Qty) }}</td>
                    <td>{{ displayVal(row.GTIN_PL_1) }}</td>
                    <td>{{ displayVal(row.GTIN_PL_2) }}</td>
                    <td>{{ displayVal(row.GTIN_PL_3) }}</td>
                    <td>{{ displayVal(row.GTIN_PL_4) }}</td>
                    <td>{{ displayVal(row.GTIN_PL_5) }}</td>
                    <td>{{ displayVal(row.GTIN_PL_6) }}</td>
                    <td>{{ displayVal(row.GTIN_PL_7) }}</td>
                    <td>{{ displayVal(row.Label_Form) }}</td>
                    <td>{{ displayVal(row.Logistic_Label_Type) }}</td>
                    <td>{{ displayVal(row.Finished_Label_Type) }}</td>
                    <td>{{ displayVal(row.Wisys_Label_Type) }}</td>

                    <td>
                      <button class="btn btn-small" @click="openEditModal(row)">
                        Edit
                      </button>
                    </td>
                  </tr>

                  <tr v-if="!loading && pagedRecords.length === 0">
                    <td colspan="17" class="empty-row">
                      No label records found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <PabloChatSidebar :loc="selectedLocation" />
    </div>

    <!-- EDIT MODAL -->
    <div v-if="showEditModal" class="popup-overlay" @click.self="closeEditModal">
      <div class="popup-content edit-modal">
        <div class="popup-header">
          <div class="modal-title-wrap">
            <h3 style="margin: 0;">Edit Label Setup</h3>
            <div class="modal-subtitle">
              Item: <strong>{{ editRecord.Item_ID }}</strong>
            </div>
          </div>

          <button class="close-btn" @click="closeEditModal">✕</button>
        </div>

        <div v-if="validationError" class="error-box">
          {{ validationError }}
        </div>

        <div class="edit-grid">
          <div class="field-block readonly">
            <label>Item No</label>
            <input type="text" v-model="editRecord.Item_ID" disabled />
          </div>

          <div class="field-block">
            <label>UPC Item ID</label>
            <input type="number" min="0" step="1" v-model="editRecord.UPC_Item_ID" />
          </div>

          <div class="field-block wide">
            <label>GTIN-14 Code</label>
            <input
              type="text"
              maxlength="14"
              v-model="editRecord.GTIN_14_Code"
              placeholder="14 digits"
            />
          </div>

          <div class="field-block">
            <label>UPC Qty</label>
            <input type="number" min="0" step="1" v-model="editRecord.UPC_Qty" />
          </div>

          <div class="field-block">
            <label>GTIN PL 1</label>
            <input type="number" min="0" step="1" v-model="editRecord.GTIN_PL_1" />
          </div>

          <div class="field-block">
            <label>GTIN PL 2</label>
            <input type="number" min="0" step="1" v-model="editRecord.GTIN_PL_2" />
          </div>

          <div class="field-block">
            <label>GTIN PL 3</label>
            <input type="number" min="0" step="1" v-model="editRecord.GTIN_PL_3" />
          </div>

          <div class="field-block">
            <label>GTIN PL 4</label>
            <input type="number" min="0" step="1" v-model="editRecord.GTIN_PL_4" />
          </div>

          <div class="field-block">
            <label>GTIN PL 5</label>
            <input type="number" min="0" step="1" v-model="editRecord.GTIN_PL_5" />
          </div>

          <div class="field-block">
            <label>GTIN PL 6</label>
            <input type="number" min="0" step="1" v-model="editRecord.GTIN_PL_6" />
          </div>

          <div class="field-block">
            <label>GTIN PL 7</label>
            <input type="number" min="0" step="1" v-model="editRecord.GTIN_PL_7" />
          </div>

          <div class="field-block">
            <label>Label Form</label>
            <input
              type="text"
              maxlength="10"
              list="labelFormOptions"
              v-model="editRecord.Label_Form"
            />
            <datalist id="labelFormOptions">
              <option v-for="x in options.Label_Form" :key="x" :value="x" />
            </datalist>
          </div>

          <div class="field-block wide">
            <label>Logistic Label Type</label>
            <input
              type="text"
              maxlength="50"
              list="logisticLabelOptions"
              v-model="editRecord.Logistic_Label_Type"
            />
            <datalist id="logisticLabelOptions">
              <option v-for="x in options.Logistic_Label_Type" :key="x" :value="x" />
            </datalist>
          </div>

          <div class="field-block wide">
            <label>Finished Label Type</label>
            <input
              type="text"
              maxlength="50"
              list="finishedLabelOptions"
              v-model="editRecord.Finished_Label_Type"
            />
            <datalist id="finishedLabelOptions">
              <option v-for="x in options.Finished_Label_Type" :key="x" :value="x" />
            </datalist>
          </div>

          <div class="field-block wide">
            <label>WiSys Label Type</label>
            <input
              type="text"
              maxlength="50"
              list="wisysLabelOptions"
              v-model="editRecord.Wisys_Label_Type"
            />
            <datalist id="wisysLabelOptions">
              <option v-for="x in options.Wisys_Label_Type" :key="x" :value="x" />
            </datalist>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn" @click="closeEditModal" :disabled="saving">
            Cancel
          </button>

          <button class="btn save-btn" @click="saveRecord" :disabled="saving">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PabloChatSidebar from './PabloChatSidebar.vue';
import PabloQuacious from './PabloQuacious.vue';

const NUMERIC_FIELDS = [
  'UPC_Item_ID',
  'UPC_Qty',
  'GTIN_PL_1',
  'GTIN_PL_2',
  'GTIN_PL_3',
  'GTIN_PL_4',
  'GTIN_PL_5',
  'GTIN_PL_6',
  'GTIN_PL_7'
];

const TEXT_FIELDS = [
  'GTIN_14_Code',
  'Label_Form',
  'Logistic_Label_Type',
  'Finished_Label_Type',
  'Wisys_Label_Type'
];

export default {
  name: 'GTINLabelSetup',

  components: {
    PabloChatSidebar,
    PabloQuacious
  },

  data() {
    return {
      loading: false,
      saving: false,

      records: [],
      searchText: '',
      statusFilter: 'ALL',

      currentPage: 1,
      pageSize: 75,

      showEditModal: false,
      editRecord: null,
      validationError: '',

      pablotifyUnread: 0,

      options: {
        Label_Form: [],
        Logistic_Label_Type: [],
        Finished_Label_Type: [],
        Wisys_Label_Type: []
      }
    };
  },

  computed: {
    selectedLocation() {
      return this.$root.selectedLocation;
    },

    filteredRecords() {
      const search = String(this.searchText || '').trim().toUpperCase();

      return (this.records || []).filter((r) => {
        const item = String(r.Item_ID || '').toUpperCase();
        const gtin = String(r.GTIN_14_Code || '').toUpperCase();

        const matchesSearch =
          !search ||
          item.includes(search) ||
          gtin.includes(search);

        const status = this.recordStatus(r);
        const matchesStatus =
          this.statusFilter === 'ALL' ||
          this.statusFilter === status;

        return matchesSearch && matchesStatus;
      });
    },

    pagedRecords() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredRecords.slice(start, start + this.pageSize);
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRecords.length / this.pageSize));
    },

    configuredCount() {
      return this.records.filter((r) => this.recordStatus(r) === 'CONFIGURED').length;
    },

    partialCount() {
      return this.records.filter((r) => this.recordStatus(r) === 'PARTIAL').length;
    },

    blankCount() {
      return this.records.filter((r) => this.recordStatus(r) === 'BLANK').length;
    }
  },

  methods: {
    displayVal(value) {
      if (value === null || value === undefined || String(value).trim() === '') {
        return '—';
      }
      return value;
    },

    isBlank(value) {
      return value === null || value === undefined || String(value).trim() === '';
    },

    hasPositivePalletQty(row) {
      return [
        row.GTIN_PL_1,
        row.GTIN_PL_2,
        row.GTIN_PL_3,
        row.GTIN_PL_4,
        row.GTIN_PL_5,
        row.GTIN_PL_6,
        row.GTIN_PL_7
      ].some((v) => Number(v || 0) > 0);
    },

    recordStatus(row) {
      const hasGtin = !this.isBlank(row.GTIN_14_Code);
      const hasLabelForm = !this.isBlank(row.Label_Form);
      const hasFinishedLabel = !this.isBlank(row.Finished_Label_Type);
      const hasWisys = !this.isBlank(row.Wisys_Label_Type);
      const hasAnyQty = this.hasPositivePalletQty(row) || Number(row.UPC_Qty || 0) > 0;

      const allOtherBlank =
        NUMERIC_FIELDS.every((f) => row[f] === null || row[f] === undefined || row[f] === '') &&
        TEXT_FIELDS.every((f) => this.isBlank(row[f]));

      if (allOtherBlank) return 'BLANK';

      if (hasGtin && hasLabelForm && hasFinishedLabel && hasWisys && hasAnyQty) {
        return 'CONFIGURED';
      }

      return 'PARTIAL';
    },

    statusClass(row) {
      const status = this.recordStatus(row);
      return {
        configured: status === 'CONFIGURED',
        partial: status === 'PARTIAL',
        blank: status === 'BLANK'
      };
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },

    async loadAll() {
      this.loading = true;

      try {
        const { data } = await axios.get('/gtinBarcodeData');
        this.records = Array.isArray(data) ? data : [];
        this.currentPage = 1;

        await this.loadOptions();
      } catch (err) {
        console.error('loadAll gtinBarcodeData error:', err);
        alert('Failed to load GTIN / label data.');
      } finally {
        this.loading = false;
      }
    },

    async loadOptions() {
      try {
        const { data } = await axios.get('/gtinBarcodeDataOptions');

        this.options = {
          Label_Form: data?.Label_Form || [],
          Logistic_Label_Type: data?.Logistic_Label_Type || [],
          Finished_Label_Type: data?.Finished_Label_Type || [],
          Wisys_Label_Type: data?.Wisys_Label_Type || []
        };
      } catch (err) {
        // Options are nice-to-have. Do not block the page if this route is not added yet.
        console.warn('gtinBarcodeDataOptions not loaded:', err?.message || err);
      }
    },

    cloneRecord(row) {
      return JSON.parse(JSON.stringify(row || {}));
    },

    openEditModal(row) {
      this.validationError = '';
      this.editRecord = this.cloneRecord(row);

      for (const field of NUMERIC_FIELDS) {
        if (this.editRecord[field] === null || this.editRecord[field] === undefined) {
          this.editRecord[field] = '';
        }
      }

      for (const field of TEXT_FIELDS) {
        if (this.editRecord[field] === null || this.editRecord[field] === undefined) {
          this.editRecord[field] = '';
        }
      }

      this.showEditModal = true;
    },

    closeEditModal() {
      if (this.saving) return;

      this.showEditModal = false;
      this.editRecord = null;
      this.validationError = '';
    },

    cleanNullableInt(value, fieldName) {
      if (value === null || value === undefined || String(value).trim() === '') {
        return null;
      }

      const n = Number(value);

      if (!Number.isInteger(n) || n < 0) {
        throw new Error(`${fieldName} must be a non-negative whole number.`);
      }

      return n;
    },

    cleanString(value, maxLength, fieldName) {
      if (value === null || value === undefined || String(value).trim() === '') {
        return null;
      }

      const cleaned = String(value).trim();

      if (cleaned.length > maxLength) {
        throw new Error(`${fieldName} cannot exceed ${maxLength} characters.`);
      }

      return cleaned;
    },

    buildPayload() {
      const r = this.editRecord || {};

      const gtin = this.cleanString(r.GTIN_14_Code, 14, 'GTIN-14 Code');

      if (gtin && !/^\d{14}$/.test(gtin)) {
        throw new Error('GTIN-14 Code must be exactly 14 digits.');
      }

      return {
        UPC_Item_ID: this.cleanNullableInt(r.UPC_Item_ID, 'UPC Item ID'),
        GTIN_14_Code: gtin,
        UPC_Qty: this.cleanNullableInt(r.UPC_Qty, 'UPC Qty'),
        GTIN_PL_1: this.cleanNullableInt(r.GTIN_PL_1, 'GTIN PL 1'),
        GTIN_PL_2: this.cleanNullableInt(r.GTIN_PL_2, 'GTIN PL 2'),
        GTIN_PL_3: this.cleanNullableInt(r.GTIN_PL_3, 'GTIN PL 3'),
        GTIN_PL_4: this.cleanNullableInt(r.GTIN_PL_4, 'GTIN PL 4'),
        GTIN_PL_5: this.cleanNullableInt(r.GTIN_PL_5, 'GTIN PL 5'),
        GTIN_PL_6: this.cleanNullableInt(r.GTIN_PL_6, 'GTIN PL 6'),
        GTIN_PL_7: this.cleanNullableInt(r.GTIN_PL_7, 'GTIN PL 7'),
        Label_Form: this.cleanString(r.Label_Form, 10, 'Label Form'),
        Logistic_Label_Type: this.cleanString(r.Logistic_Label_Type, 50, 'Logistic Label Type'),
        Finished_Label_Type: this.cleanString(r.Finished_Label_Type, 50, 'Finished Label Type'),
        Wisys_Label_Type: this.cleanString(r.Wisys_Label_Type, 50, 'WiSys Label Type')
      };
    },

    async saveRecord() {
      this.validationError = '';

      if (!this.editRecord || !this.editRecord.Item_ID) {
        this.validationError = 'Missing Item_ID.';
        return;
      }

      let payload;

      try {
        payload = this.buildPayload();
      } catch (err) {
        this.validationError = err.message;
        return;
      }

      this.saving = true;

      try {
        const itemId = encodeURIComponent(String(this.editRecord.Item_ID).trim());

        const { data } = await axios.put(`/gtinBarcodeData/${itemId}`, payload);

        const updated = data?.record || null;

        if (updated) {
          const idx = this.records.findIndex(
            (r) => String(r.Item_ID).trim() === String(updated.Item_ID).trim()
          );

          if (idx !== -1) {
            this.records.splice(idx, 1, updated);
          }
        } else {
          await this.loadAll();
        }

        this.showEditModal = false;
        this.editRecord = null;
        this.validationError = '';
      } catch (err) {
        console.error('saveRecord error:', err);

        const msg =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to save GTIN / label record.';

        this.validationError = msg;
      } finally {
        this.saving = false;
      }
    }
  },

  mounted() {
    this.loadAll();
  }
};
</script>

<style scoped>
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  margin-top: 20px;
  background-color: #0b0101;
  min-height: 100vh;
  padding-bottom: 40px;
  align-items: stretch !important;
  text-align: left !important;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
  text-align: center;
}

.gtin-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.gtin-layout {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-right: 10px;
  color: #fff;
  margin-top: 80px;
}

.gtin-wrapper {
  width: 100%;
  min-width: 0;
}

.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control select,
.control input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  min-width: 180px;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.btn,
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 125px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}

.btn:hover:not(:disabled),
.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.btn:disabled,
.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.btn-small {
  width: auto;
  padding: 6px 12px;
  font-size: 13px;
}

.save-btn {
  background: #2ea043;
  color: #fff;
  border-color: #2ea043;
}

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background: rgb(67, 67, 67);
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.8;
  line-height: 1.1;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #cbbfbf;
  line-height: 1.1;
  margin-top: 2px;
}

.table-panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 12px;
  text-align: left;
}

.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
}

.table-scroll {
  max-height: 78vh;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid #333;
}

.gtin-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1500px;
}

.gtin-table th,
.gtin-table td {
  border: 1px solid #333;
  padding: 7px 8px;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
}

.gtin-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
}

.gtin-table tr:nth-child(even) td {
  background: #0f141c;
}

.gtin-table tr:nth-child(odd) td {
  background: #0b0101;
}

.item-cell {
  font-weight: bold;
  color: #e5e7eb;
}

.gtin-cell {
  font-family: monospace;
  letter-spacing: 0.5px;
}

.empty-row {
  text-align: center !important;
  padding: 18px !important;
  opacity: 0.75;
}

.status-pill {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #333;
  font-weight: bold;
}

.status-pill.configured {
  background: #12351e;
  color: #7aff9a;
  border-color: #2ea043;
}

.status-pill.partial {
  background: #3a2d09;
  color: #ffd166;
  border-color: #9a7b16;
}

.status-pill.blank {
  background: #111827;
  color: #cbbfbf;
  border-color: #444;
}

.pager {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-text,
.small-muted {
  font-size: 12px;
  opacity: 0.75;
  color: #cbbfbf;
}

.side-help {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px;
  background: rgb(67, 67, 67);
  font-size: 13px;
  line-height: 1.6;
  color: #cbbfbf;
}

.notif-badge {
  margin-left: 4px;
}

/* Modal */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #0b0101;
  padding: 10px 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  color: #cbbfbf;
}

.edit-modal {
  width: 900px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #cbbfbf;
  font-size: 20px;
  cursor: pointer;
  font-weight: bold;
}

.modal-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.modal-subtitle {
  font-size: 12px;
  opacity: 0.8;
  color: #cbbfbf;
}

.edit-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 12px;
  text-align: left;
}

.field-block {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-block.wide {
  grid-column: span 2;
}

.field-block label {
  font-size: 13px;
  font-weight: bold;
  color: #cbbfbf;
}

.field-block input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background: #111;
  color: #cbbfbf;
  font-family: inherit;
  box-sizing: border-box;
}

.field-block.readonly input {
  opacity: 0.65;
  cursor: not-allowed;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.error-box {
  background: #2a0b0b;
  border: 1px solid #aa4444;
  color: #fca5a5;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 12px;
  text-align: left;
  font-size: 13px;
}
</style>